import { FC } from 'react';
import { asset } from '../../../../../../shared/frontend/services/assetResolverService';
import { withSiteMeta } from '../../../../../../shared/frontend/HOCS/withSiteMeta';
import EmbeddedYoutubeVideoPlaylist from '../presentational/EmbeddedYoutubeVideoPlaylist';
import { DynamicPaneContent } from '../../../../../../shared/frontend/components/presentational/layouts';
import { ZoneContainer } from '../../../../../../shared/frontend/components/ads/zone';
import { HomeContent, THomeContentProps } from './homeContent';
import { HouseAds } from '../../../../../../shared/frontend/components/ads/houseAds';
import { SidebarHouseAd } from '../presentational/houseAds/sidebarHouseAd';
import { getClassSpecBySiteMetaAndConfig } from './base-main-page-components/mythicPlusTopRunsPane/utils';

const MAX_NEWS_ENTRIES = 5;

function getHomeContentParams(props: any) {
  const mainSeasonSlug = 'season-mn-1';
  const mainRaidSlug = 'sporefall';
  const secondaryRaidSlug = 'tier-mn-1';
  const region = 'world';
  const expansion = 'midnight';
  const realm = undefined;

  const { cls, spec } = getClassSpecBySiteMetaAndConfig(props.siteMeta, props.siteConfig);

  const homeContentParams: THomeContentProps = {
    includeFeaturedStreams: {
      enabled: true
    },
    includeDescription: {
      enabled: !props.currentFeatures.authenticated,
      expansion,
      raidSlug: mainRaidSlug,
      seasonSlug: mainSeasonSlug
    },
    includeNews: {
      enabled: false,
      maxEntries: MAX_NEWS_ENTRIES
    },
    includeMythicPlusTeams: {
      enabled: true,
      seasonSlug: mainSeasonSlug,
      region
    },
    includeRemix: {
      enabled: false,
      seasonSlug: 'season-tww-3-legion-remix',
      soloSeasonSlug: 'season-tww-3-legion-remix-1-player',
      region,
      realm
    },
    includeRecruitment: {
      enabled: true,
      region
    },
    includeRaidExpansionHistory: {
      enabled: false,
      expansion: 'the-war-within',
      raidSlugs: ['nerubar-palace', 'liberation-of-undermine', 'manaforge-omega'],
      difficulty: 'mythic',
      region,
      realm
    },
    includeFatedRaids: {
      enabled: false,
      title: 'frontpage:section-header-top-awakened-guilds',
      seasonSlug: mainSeasonSlug,
      raidSlugs: [
        'awakened-vault-of-the-incarnates',
        'awakened-aberrus-the-shadowed-crucible',
        'awakened-amirdrassil-the-dreams-hope'
      ],
      difficulty: props.siteConfig.homePageRaidDifficulty,
      region,
      realm
    },
    includeRaidDuo: {
      enabled: true,
      raidSlug: mainRaidSlug,
      seasonSlug: mainSeasonSlug,
      difficulty: props.siteConfig.homePageRaidDifficulty,
      overrideImageSlug: mainRaidSlug,
      region,
      realm
    },
    includeRaidPartitions: {
      enabled: true,
      raidSlug: mainRaidSlug,
      seasonSlug: mainSeasonSlug,
      difficulty: props.siteConfig.homePageRaidDifficulty,
      region,
      realm
    },
    includeSecondaryRaidDuo: {
      enabled: true,
      raidSlug: secondaryRaidSlug,
      seasonSlug: mainSeasonSlug,
      difficulty: props.siteConfig.homePageRaidDifficulty,
      overrideImageSlug: secondaryRaidSlug,
      region,
      realm
    },
    includeSecondaryRaidPartitions: {
      enabled: true,
      raidSlug: secondaryRaidSlug,
      seasonSlug: mainSeasonSlug,
      difficulty: props.siteConfig.homePageRaidDifficulty,
      region,
      realm
    },
    includeRWFVisualizer: {
      enabled: !!props.currentFeatures.rwf_homepage_visualizer,
      raidSlug: secondaryRaidSlug,
      difficulty: props.siteConfig.homePageRaidDifficulty,
      region
    },
    includeMythicPlusRecentRuns: {
      enabled: true,
      seasonSlug: mainSeasonSlug,
      region,
      realm
    },
    includeMythicPlusTopPlayers: {
      enabled: true,
      seasonSlug: mainSeasonSlug,
      region
    },
    includeTopItemLevelAndAchievement: {
      enabled: !!props.currentFeatures.homepage_ilvl_rankings,
      region,
      realm,
      limit: 5
    },
    includeMythicPlusTopAffixRuns: {
      enabled: false,
      seasonSlug: mainSeasonSlug,
      region,
      realm
    },
    includeMythicPlusTopRuns: {
      enabled: true,
      seasonSlug: mainSeasonSlug,
      region,
      realm,
      topSpecParams: {
        classSlug: cls.slug,
        specSlug: spec.slug
      }
    }
  };

  return homeContentParams;
}

const HomePageBase: FC<any> = (props) => {
  const homeContentParams = getHomeContentParams({
    currentFeatures: props.currentFeatures,
    siteMeta: props.siteMeta,
    siteConfig: props.siteConfig
  });

  return (
    <DynamicPaneContent
      allowCollapse={false}
      className="rio-homepage-container"
      style={{
        backgroundImage: `url(${asset('/images/bgtiles/stone.jpg')})`
      }}
    >
      <>
        <HomeContent {...homeContentParams} />
      </>
      <>
        <ZoneContainer tag="section" className="slds-text-align--center" type="mrec_sidebar_top_short" />
        <HouseAds type="sidebar" width={300} height={90} component={SidebarHouseAd} />
        {props.currentFeatures.params.embedded_youtube_playlist_id && (
          <EmbeddedYoutubeVideoPlaylist playListId={props.currentFeatures.params.embedded_youtube_playlist_id} />
        )}
        <ZoneContainer tag="section" className="slds-text-align--center" sticky={true} type="mrec_sidebar_bottom" />
      </>
    </DynamicPaneContent>
  );
};

const HomePage = withSiteMeta(HomePageBase);

(HomePage as any).serverPreloadActions = (params: any) => {
  const { siteMeta, currentFeatures, siteConfig } = params.state;

  const homeContentParams = getHomeContentParams({
    currentFeatures,
    siteMeta,
    siteConfig
  });

  return (HomeContent as any).preloadComponentActions(homeContentParams, params);
};

export default HomePage;
